// File pass (2026-10-01). Downloads are members-only, and the middleware enforces that with
// the session cookie. But some browsers hand a file to a DIFFERENT browser that has no
// cookie: the LinkedIn app's built-in browser passes PDFs to Chrome/Safari, so a member who
// had just logged in hit the login wall again (seen 2026-09-18, three logins in two minutes).
// A logged-in member's file links therefore carry a short-lived signed pass the middleware
// accepts in place of the cookie. Only /api/file-pass mints one, and only for a verified
// session, so the rule "no download without login" holds: no login, no pass.
//
// The pass is `<expiry epoch seconds>.<HMAC-SHA256 of that expiry>`, keyed by
// FILE_PASS_SECRET. Web Crypto only, so the same code runs in the edge middleware and in
// the Node route. No secret configured = no passes minted and none accepted (cookie only).
export const FILE_PASS_PARAM = 'pass'
/** Minutes-scale on purpose: long enough to tap a link on a page that refreshes its pass
 *  every couple of minutes, short enough that a forwarded link is dead almost at once. */
export const FILE_PASS_TTL_SECONDS = 300

const encoder = new TextEncoder()
const payload = (exp: number) => encoder.encode(`ea-file-pass.v1.${exp}`)

const hmacKey = (secret: string, usage: 'sign' | 'verify') =>
  crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    usage,
  ])

const toBase64Url = (bytes: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

const fromBase64Url = (s: string): Uint8Array => {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
  return Uint8Array.from(bin, (c) => c.charCodeAt(0))
}

export async function mintFilePass(
  secret: string,
  nowMs: number = Date.now(),
): Promise<{ pass: string; expiresAt: number }> {
  const exp = Math.floor(nowMs / 1000) + FILE_PASS_TTL_SECONDS
  const sig = await crypto.subtle.sign('HMAC', await hmacKey(secret, 'sign'), payload(exp))
  return { pass: `${exp}.${toBase64Url(sig)}`, expiresAt: exp * 1000 }
}

export async function isValidFilePass(
  secret: string,
  pass: string,
  nowMs: number = Date.now(),
): Promise<boolean> {
  const m = /^(\d{10})\.([A-Za-z0-9_-]{43})$/.exec(pass)
  if (!m) return false
  const exp = Number(m[1])
  if (exp * 1000 <= nowMs) return false
  try {
    // subtle.verify compares in constant time
    return await crypto.subtle.verify(
      'HMAC',
      await hmacKey(secret, 'verify'),
      fromBase64Url(m[2] as string),
      payload(exp),
    )
  } catch {
    return false
  }
}
